import React, { Component } from 'react'
import { Container, Row, Col } from 'reactstrap'
import ReturnsList from "../CA/ReturnsList"
import '../CA/CarsList.css'
import RentalsListHeader from "../Rentals/RentalListNew/RentalsListHeader"
import ListingTabs from "../Material/ListingTabs"
import { buildPresets } from "../Filters/presetFilters"

import withApolloClient from '../withApolloClient'

// Each tab's default filters, expressed as Advanced Filters presets -- the same
// FilterInput[] shape the query already sends for user filters, so the popover,
// the chips and Reset all read one source of truth. See IncidentsListContainer
// for the pattern this follows.
//
// fieldName is "stage", not "return_stage": CarReturn has both fields, but the
// legacy returnStage resolver argument has always filtered stage__in (see
// resolve_all_returns in cars/schema.py) -- return_stage instead records the
// *car's* stage on return day. Using it here would silently filter the wrong
// column.
export const RETURN_LISTING_TABS = [
    {
        id: "#awaitingToCloseout", name: "Awaiting To Closeout", iconClassName: "fa fa-reply",
        orderBy: ["-agreement__driver__in_office", "-id"],
        getPresetFilters: () => [
            { fieldName: "stage", filterType: "in", filterValues: [3], editable: true },
        ],
    },
    {
        id: "#incompleteReturns", name: "Incomplete Returns", iconClassName: "fa fa-hourglass-half",
        orderBy: ["dateAdded", "-id"],
        getPresetFilters: () => [
            { fieldName: "stage", filterType: "in", filterValues: [5], editable: true },
            { fieldName: "return_type", filterType: "in", filterValues: ["Return", "Switch"], editable: true },
        ],
    },
    {
        id: "#lost", name: "Lost", iconClassName: "fa fa-times",
        orderBy: ["dateAdded", "-id"],
        getPresetFilters: () => [
            { fieldName: "return_type", filterType: "equal", filterValues: "Lost", editable: true },
        ],
    },
    {
        id: "#all", name: "All Returns", iconClassName: "fa fa-calendar-o",
        orderBy: ["-dateAdded", "-agreement__driver__in_office"],
        getPresetFilters: () => [],
    },
]

export const getPresetsForTab = (tabId) =>
    buildPresets(RETURN_LISTING_TABS.find(tab => tab.id === tabId)?.getPresetFilters() || [])

// allReturns still takes the legacy scalar arguments (returnStage, returnType,
// returnTypeIn) alongside the generic `filters` argument -- see
// resolve_all_returns in cars/schema.py. ReturnsList.js now combines this
// container's `presets` prop with the URL's advancedFilters into `filters`
// (see combineFilters in Filters/presetFilters and ReturnsList.js), so the
// active tab's presets reach the query that way. The legacy scalar args are
// left alone for Available Cars / Scheduled Returns (frontend/src/CA/AvailableCars),
// which still relies on them and passes no `presets` prop -- but this
// container itself no longer sends them, to avoid filtering the same field
// twice.
class CarReturnListContainer extends Component {
    constructor(props) {
        super(props)
        // presets and defaultPresets start out equal -- setPresets only ever
        // updates presets, so defaultPresets stays the active tab's declared
        // set and is what Reset returns to.
        const initialPresets = getPresetsForTab(RETURN_LISTING_TABS[0].id)
        this.state = {
            activeTab: null,
            searchTerm: "",
            selectedIds: [],
            filterValues: {
                orderBy: RETURN_LISTING_TABS[0].orderBy,
            },
            presets: initialPresets,
            defaultPresets: initialPresets,
            refetchReservations: false,
        }
    }
    updateOrder = (string) => {
        let order = [...this.state.filterValues.orderBy]
        if (!order.includes(string) && !order.includes("-" + string)) {
            order = [string, order.find(item => item.includes("-agreement__driver__in_office"))]
        } else {
            let index = order.indexOf(string)
            if (order.indexOf(string) > -1) {
                order[index] = "-" + string
            } else {
                index = order.indexOf("-" + string)
                index > -1 && (order[index] = string)
            }
        }
        this.setState({ filterValues: { ...this.state.filterValues, orderBy: order } })
    }
    updateSelection = (selection) => {
        if (Array.isArray(selection)) {
            this.setState({ selectedIds: selection })
        } else {
            if (this.state.selectedIds.includes(selection))
                this.setState({ selectedIds: this.state.selectedIds.filter(item => item != selection) })
            else
                this.setState({ selectedIds: [...this.state.selectedIds, selection] })
        }
    }
    setPresets = (presets) => this.setState({ presets })
    componentDidMount() {
        if (window.location.hash && RETURN_LISTING_TABS.map(item => item.id).includes(window.location.hash)) {
            let tab = RETURN_LISTING_TABS.find(tab => tab.id === window.location.hash)
            const presets = getPresetsForTab(tab.id)
            this.setState({ filterValues: { orderBy: tab.orderBy }, activeTab: tab.name, presets, defaultPresets: presets })
        } else {
            window.location.hash = RETURN_LISTING_TABS[0].id
            this.setState({ filterValues: { orderBy: RETURN_LISTING_TABS[0].orderBy }, activeTab: RETURN_LISTING_TABS[0].name })
        }
    }
    changeTab = (value) => {
        let tab = RETURN_LISTING_TABS.find(tab => tab.name === value)
        window.location.hash = tab.id
        const presets = getPresetsForTab(tab.id)
        this.setState({ activeTab: value, filterValues: { orderBy: tab.orderBy }, presets, defaultPresets: presets })
    }
    render() {
        return (
            <Container fluid className="CarListContainer">
                <ListingTabs
                    tabs={RETURN_LISTING_TABS}
                    activeTab={this.state.activeTab}
                    activeValueKey="name"
                    onChange={this.changeTab}
                />
                <RentalsListHeader
                    activeTab={this.state.activeTab}
                    filterValues={this.state.filterValues}
                    setFilterValues={(filterValues) => this.setState({ filterValues })}
                    location={this.props.location} history={this.props.navigate}
                    presets={this.state.presets}
                    setPresets={this.setPresets}
                    defaultPresets={this.state.defaultPresets}
                    selectedDrivers={this.state.selectedIds}
                    resetSelectedDrivers={() => this.setState({ selectedIds: [] })}
                />

                <Row className="pb-2">
                    <Col sm="12">
                        {RETURN_LISTING_TABS.map(tab => tab.name).includes(this.state.activeTab) &&
                            <ReturnsList
                                currentUser={this.props.currentUser}
                                searchTerm={this.state.filterValues && this.state.filterValues.searchTerm ? this.state.filterValues.searchTerm : null}
                                selectedDrivers={this.state.selectedIds}
                                updateDriversSelection={this.updateSelection}
                                orderBy={this.state.filterValues && this.state.filterValues.orderBy ? this.state.filterValues.orderBy : null}
                                presets={this.state.presets}
                                updateOrder={this.updateOrder}
                                activeTab={this.state.activeTab ? this.state.activeTab : ""}
                            />
                        }
                    </Col>
                </Row>
            </Container>
        )
    }
}


export default (withApolloClient(CarReturnListContainer))
